import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, api, ensureCsrf } from '../lib/api';
import { identifyUser, track, resetAnalytics } from './../lib/analytics';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [_token, setToken] = useState(localStorage.getItem('token'));

  // Boot: fetch CSRF token so the first write call has a header to attach.
  useEffect(() => {
    ensureCsrf();
  }, []);

  useEffect(() => {
    // Try /auth/me regardless of localStorage token — cookie may be present
    // for users who logged in via the new path. The interceptor adds Bearer
    // when localStorage has a token; otherwise the cookie carries auth.
    authAPI
      .getMe()
      .then((res) => {
        setUser(res.data);
        identifyUser(res.data.user_id, {
          user_type: res.data.user_type,
          is_admin: !!res.data.is_admin,
        });
      })
      .catch(() => {
        localStorage.removeItem('token');
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (credentials) => {
    const res = await authAPI.login(credentials);
    // Hybrid: cookie is set by the server, but we still keep the Bearer in
    // localStorage so the interceptor can fall back when cookies are blocked
    // (e.g. embedded webviews, headless tests).
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    // Refresh in-memory CSRF token from the new cookie set by the response.
    await ensureCsrf();
    identifyUser(res.data.user.user_id, {
      user_type: res.data.user.user_type,
      is_admin: !!res.data.user.is_admin,
    });
    track('user_logged_in', { user_type: res.data.user.user_type });
    return res.data;
  };

  const register = async (data) => {
    const res = await authAPI.register(data);
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    await ensureCsrf();
    identifyUser(res.data.user.user_id, {
      user_type: res.data.user.user_type,
      is_admin: false,
    });
    track('user_registered', {
      user_type: res.data.user.user_type,
      referred: !!res.data.user.referred_by_code,
    });
    return res.data;
  };

  const logout = async () => {
    track('user_logged_out');
    resetAnalytics();
    // Server clears the httpOnly cookie; we clear the legacy token locally.
    try {
      await api.post('/auth/logout');
    } catch (err) {
      // Network error during logout is fine — local state still cleared
      // below. Log for visibility (iter-53 review).
      console.debug('[logout] server-side cookie clear failed:', err?.message);
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, isAuthenticated: !!user }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
